import { computed, inject, Injectable, signal } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';

const MOBILE_QUERY = '(max-width: 767.98px)';
const TABLET_QUERY = '(min-width: 768px) and (max-width: 991.98px)';

/**
 * Shell breakpoint tiers (Bootstrap-aligned, see REDESIGN_PLAN.md):
 * mobile < 768px (drawer + bottom nav), tablet 768-991px (collapsed rail),
 * desktop >= 992px (full sidebar). Single source of truth for the app shell —
 * components read the signals instead of window.innerWidth.
 */
@Injectable({ providedIn: 'root' })
export class LayoutService {
  private breakpointObserver = inject(BreakpointObserver);
  private router = inject(Router);

  private breakpointState = toSignal(
    this.breakpointObserver.observe([MOBILE_QUERY, TABLET_QUERY]),
  );

  readonly isMobile = computed(
    () => this.breakpointState()?.breakpoints[MOBILE_QUERY] ?? false,
  );
  readonly isTablet = computed(
    () => this.breakpointState()?.breakpoints[TABLET_QUERY] ?? false,
  );
  readonly isDesktop = computed(() => !this.isMobile() && !this.isTablet());

  /** Mobile off-canvas menu state. */
  readonly drawerOpen = signal(false);

  constructor() {
    // Any navigation closes the drawer (tapping a menu item navigates).
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.closeDrawer();
      }
    });
  }

  toggleDrawer() {
    this.drawerOpen.update((open) => !open);
  }

  closeDrawer() {
    this.drawerOpen.set(false);
  }
}
